import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import { svgPathProperties } from 'svg-path-properties';
import { Points, Lines } from '@/types';
import {
  CanvasDraw,
  PathConfig,
  Animation,
  AnimationConfig,
} from './CanvasDraw';
import SunIconSrc from '../assets/sun.png';

export type TraceSeed =
  | 'topLeft'
  | 'top'
  | 'topRight'
  | 'right'
  | 'bottomRight'
  | 'bottom'
  | 'bottomLeft'
  | 'left';

const pointerPath = `
M27.25,4.655
C20.996-1.571,10.88-1.546,4.656,4.706
C-1.571,10.96-1.548,21.076,4.705,27.3
c6.256,6.226,16.374,6.203,22.597-0.051
C33.526,20.995,33.505,10.878,27.25,4.655z
`;

const MAX_EXPECTED_DISTANCE = 25000;
const MIN_POINTS_FOR_COMPARISON = 100;
const ITERATOR_FACTOR = 2;
const PERCENTAGE_FACTOR = 100;
const SCALE_FACTOR = 6;

function dtwDistance(path1: Points, path2: Points): number {
  const dtw = Array(path1.length + 1)
    .fill(null)
    .map(() => Array(path2.length + 1).fill(Infinity));
  dtw[0][0] = 0;

  for (let i = 1; i <= path1.length; i++) {
    for (let j = 1; j <= path2.length; j++) {
      const cost = Math.sqrt(
        (path1[i - 1].x - path2[j - 1].x) ** ITERATOR_FACTOR +
          (path1[i - 1].y - path2[j - 1].y) ** ITERATOR_FACTOR,
      );
      dtw[i][j] =
        cost + Math.min(dtw[i - 1][j], dtw[i][j - 1], dtw[i - 1][j - 1]);
    }
  }

  const dtwDistance = dtw[path1.length][path2.length];
  const score = PERCENTAGE_FACTOR * (1 - dtwDistance / MAX_EXPECTED_DISTANCE);

  return Math.round(Math.max(0, Math.min(score, PERCENTAGE_FACTOR)));
}

const resample = (points: Points, targetLength: number) => {
  const properties = new svgPathProperties(
    `M${points.map((p) => `${p.x},${p.y}`).join(' L')}`,
  );
  const totalLength = properties.getTotalLength();
  const resampledPoints = [];

  for (let i = 0; i < targetLength; i++) {
    const point = properties.getPointAtLength((i / targetLength) * totalLength);
    resampledPoints.push({ x: point.x, y: point.y });
  }

  return resampledPoints;
};

const calcAccuracyScore = (
  basisPoints: Points,
  tracePoints: Points,
  scale = 1,
) =>
  dtwDistance(
    resample(basisPoints, MIN_POINTS_FOR_COMPARISON * scale),
    resample(tracePoints, MIN_POINTS_FOR_COMPARISON * scale),
  );

const convertPathToPoints = (
  path: string | Array<string>,
  pointsLength = 0,
  xOffset = 0,
  yOffset = 0,
): Points => {
  const paths = Array.isArray(path) ? path : [path];
  const allPoints = paths.map((p) => {
    const properties = new svgPathProperties(p);
    const length = properties.getTotalLength();

    const numberOfPoints = Math.max(
      pointsLength,
      length,
      MIN_POINTS_FOR_COMPARISON,
    );

    const points = [];

    for (let i = 0; i < numberOfPoints; i++) {
      const point = properties.getPointAtLength((i / numberOfPoints) * length);

      points.push({
        x: point.x * SCALE_FACTOR + xOffset,
        y: point.y * SCALE_FACTOR + yOffset,
      });
    }

    return points;
  });

  return allPoints.reduce((acc, points) => acc.concat(points), []);
};

interface CanvasTraceProps {
  className?: string;
  currentColor?: string;
  traceBasis?: string | Array<string>;
  showBasis?: boolean;
  basisColor?: string;
  traceGuide?: string;
  showGuide?: boolean;
  additionalPrefixPaths?: Array<PathConfig>;
  additionalSuffixPaths?: Array<PathConfig>;
  seed?: TraceSeed;
  drawWidth?: number;
  drawHeight?: number;
  includeBgOnLines?: boolean;
  xOffset?: number;
  yOffset?: number;
  showScore?: boolean;
  onScoreChange?: (score: number) => void;
}

export interface CanvasTrace {
  reset: () => void;
  animateGuide: () => void;
  asBitmap: () => string | undefined;
  asVector: () => Lines | undefined;
}

export const CanvasTrace = forwardRef<CanvasTrace, CanvasTraceProps>(
  (
    {
      className,
      currentColor = '#000000',
      traceBasis,
      showBasis = true,
      basisColor = '#116C4F',
      traceGuide,
      showGuide = true,
      additionalPrefixPaths = [],
      additionalSuffixPaths = [],
      seed,
      drawWidth,
      drawHeight,
      includeBgOnLines = true,
      xOffset = 0,
      yOffset = 0,
      showScore = true,
      onScoreChange,
    },
    ref,
  ) => {
    const drawRef = useRef<CanvasDraw | null>(null);
    const animationRef = useRef<Animation | null>(null);
    const [accuracyScore, setAccuracyScore] = useState<number>(0);
    const [outputLines, setOutputLines] = useState<Lines>([]);

    const guideAnimation: AnimationConfig | undefined = traceBasis
      ? {
          ref: animationRef,
          imagePath: pointerPath,
          animationPoints: convertPathToPoints(
            traceBasis,
            0,
            xOffset - 4,
            yOffset + 31,
          ),
          loop: false,
        }
      : undefined;

    useImperativeHandle(ref, () => ({
      reset: () => {
        drawRef.current?.reset();
        setAccuracyScore(0);
        onScoreChange?.(0);
      },
      animateGuide: () => {
        animationRef.current?.play();
      },
      asBitmap: () => drawRef.current?.asBitmap(),
      asVector: () => outputLines,
    }));

    useEffect(() => {
      if (!traceBasis || !outputLines?.length) {
        return;
      }

      const concatPoints = outputLines.reduce(
        (acc: Points, line) => acc.concat(line.points),
        [],
      );

      const accuracyScore = calcAccuracyScore(
        convertPathToPoints(traceBasis, concatPoints.length, xOffset, yOffset),
        concatPoints,
        Array.isArray(traceBasis) ? traceBasis.length : 1,
      );

      setAccuracyScore(accuracyScore);
      onScoreChange?.(accuracyScore);
    }, [traceBasis, outputLines, onScoreChange, xOffset, yOffset]);

    return (
      <div className={clsx('relative', className)}>
        {seed && (
          <Image
            src={SunIconSrc}
            width={48}
            height={48}
            alt="Sun icon indicating starting point"
            className={clsx(
              'absolute z-10',
              seed === 'topLeft' && 'top-0 left-0',
              seed === 'top' && 'top-0 left-1/2 transform -translate-x-1/2',
              seed === 'topRight' && 'top-0 right-0',
              seed === 'right' && 'top-1/2 right-0 transform -translate-y-1/2',
              seed === 'bottomRight' && 'bottom-0 right-0',
              seed === 'bottom' &&
                'bottom-0 left-1/2 transform -translate-x-1/2',
              seed === 'bottomLeft' && 'bottom-0 left-0',
              seed === 'left' && 'top-1/2 left-0 transform -translate-y-1/2',
            )}
          />
        )}

        <CanvasDraw
          ref={drawRef}
          currentColor={currentColor}
          width={drawWidth}
          height={drawHeight}
          inputPaths={[
            ...additionalPrefixPaths,
            ...(traceBasis
              ? (Array.isArray(traceBasis) ? traceBasis : [traceBasis]).map(
                  (path) => ({
                    path,
                    xOffset: xOffset,
                    yOffset: yOffset,
                    strokeWidth: 0.5,
                    dashed: true,
                    color: basisColor,
                    hidden: !showBasis,
                  }),
                )
              : []),
            ...(traceGuide
              ? [
                  {
                    path: traceGuide,
                    color: '#000000',
                    fillColor: '#ffffff',
                    hidden: !showGuide,
                    strokeWidth: 0.6,
                  },
                ]
              : []),
            ...additionalSuffixPaths,
          ]}
          animations={guideAnimation ? [guideAnimation] : undefined}
          includeBgOnLines={includeBgOnLines}
          setOutputLines={setOutputLines}
        />
        {showScore && <>Score: {accuracyScore}</>}
      </div>
    );
  },
);

CanvasTrace.displayName = 'CanvasTrace';
